import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import Modal from '../../components/common/Modal.jsx';
import apiClient from '../../services/api/apiClient.js';
import { exportTablePdf, exportTableExcel } from '../../utils/exportUtils.js';

/**
 * "Estado de Cuenta" / saldo corrido de un Banco -- TAREA 4 de la segunda ronda de la
 * auditoría CxC 2026-09-28 (ver DUDAS.md, gap #8): V1
 * (`IbuFront/.../Bancos/ListadoBancos.jsx`, modal `modalMovs`/`jalaMovimientos`/
 * `PDFOC`/`excelFOC`, líneas ~336-355 y ~893-1098) tiene, por fila de Banco, un botón
 * que abre un modal con el detalle de movimientos + saldo corrido (columna "Saldo"
 * acumulada fila por fila) + "Saldo Anterior"/"Cargos del Periodo"/"Abonos del
 * Periodo"/"Saldo Actual" + export PDF/Excel -- `MovimientosBancosPage.jsx` (listado
 * plano por rutas, ver DUDAS.md D-11.5) no tenía ningún equivalente por banco.
 *
 * Mismo patrón que `pages/socios-retiros/EstadoCuentaSocioModal.jsx`/
 * `pages/reportes/EstadoCuentaClientePage.jsx` (saldo corrido calculado en cliente, el
 * backend no lo persiste).
 *
 * Backend: `GET /movimientos-bancos/banco/:id` (`movimientos-bancos.service.js
 * #listarPorBanco`) ya devuelve el feed completo, ordenado ASC por `fecha`, con
 * `{fecha, movimiento, observaciones, numero, cargo, comision, abono}` -- a
 * diferencia del kardex de Clientes/Socios, aquí `cargo`/`comision` YA vienen con el
 * signo correcto (negativos = salida de dinero, ver comentario de ese service), así
 * que el saldo corrido es una SUMA simple (`cargo + comision + abono`), no la resta
 * que usan los otros dos kardex (que reciben magnitudes siempre positivas).
 */
export default function EstadoCuentaBancoModal({ banco, onClose }) {
  const [movs, setMovs] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [exportando, setExportando] = useState(null);
  const [fechaInicio, setFechaInicio] = useState('');

  useEffect(() => {
    let cancelado = false;
    setCargando(true);
    setError(null);
    apiClient.get(`/movimientos-bancos/banco/${banco._id}`)
      .then(({ data }) => { if (!cancelado) setMovs(data.data || []); })
      .catch((e) => { if (!cancelado) setError(e.message); })
      .finally(() => { if (!cancelado) setCargando(false); });
    return () => { cancelado = true; };
  }, [banco._id]);

  const { filas, saldoAnterior } = useMemo(() => {
    const previos = fechaInicio ? movs.filter((m) => m.fecha < fechaInicio) : [];
    const posteriores = fechaInicio ? movs.filter((m) => m.fecha >= fechaInicio) : movs;

    const anterior = previos.reduce((s, m) => s + (m.cargo || 0) + (m.comision || 0) + (m.abono || 0), 0);

    let saldo = anterior;
    const filasCalc = posteriores.map((m) => {
      saldo = saldo + (m.cargo || 0) + (m.comision || 0) + (m.abono || 0);
      return { ...m, saldoCorrido: saldo };
    });

    if (fechaInicio) {
      // V1: renglón "NA ... Saldo Anterior (en Observaciones) ... NA" con el saldo inicial.
      filasCalc.unshift({
        fecha: 'NA', movimiento: 'NA', observaciones: 'Saldo Anterior', numero: 'NA',
        cargo: 'NA', comision: 'NA', abono: 'NA', saldoCorrido: anterior, esSaldoAnterior: true,
      });
    }

    return { filas: filasCalc, saldoAnterior: anterior };
  }, [movs, fechaInicio]);

  const totales = useMemo(
    () => filas.filter((f) => !f.esSaldoAnterior).reduce((t, f) => ({
      cargo: t.cargo + (f.cargo || 0), comision: t.comision + (f.comision || 0), abono: t.abono + (f.abono || 0),
    }), { cargo: 0, comision: 0, abono: 0 }),
    [filas]
  );
  const saldoActual = filas.length ? filas[filas.length - 1].saldoCorrido : saldoAnterior;
  const saldoBanco = Number(banco.abonos || 0) - Number(banco.cargos || 0);

  const money = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(Number(n || 0));

  const fmtFecha = (f) => { // V1 `formatDate`: YYYY-MM-DD -> DD-MM-YYYY
    const p = String(f || '').split('-');
    return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : (f || '');
  };
  const moneyNA = (n) => (n === 'NA' ? 'NA' : money(n));
  const filasDatos = filas.filter((f) => !f.esSaldoAnterior);
  // PDF de V1: Fecha, Movimiento, Observaciones, Numero, Cargos, Abonos, Saldo (sin Comision).
  const columnasPdf = [
    { key: 'fecha', label: 'Fecha' },
    { key: 'movimiento', label: 'Movimiento' },
    { key: 'observaciones', label: 'Observaciones' },
    { key: 'numero', label: 'Numero' },
    { key: 'cargo', label: 'Cargos', render: (r) => money(r.cargo) },
    { key: 'abono', label: 'Abonos', render: (r) => money(r.abono) },
    { key: 'saldoCorrido', label: 'Saldo', render: (r) => money(r.saldoCorrido) },
  ];
  // Excel de V1: Fecha, Movimiento, Observaciones, Numero, Cargos, Comision, Abonos.
  const columnasExcel = [
    { key: 'fecha', label: 'Fecha' },
    { key: 'movimiento', label: 'Movimiento' },
    { key: 'observaciones', label: 'Observaciones' },
    { key: 'numero', label: 'Numero' },
    { key: 'cargo', label: 'Cargos' },
    { key: 'comision', label: 'Comision' },
    { key: 'abono', label: 'Abonos' },
  ];

  function exportarPDF() {
    setExportando('pdf');
    try {
      exportTablePdf(`Estado de Cuenta - Banco ${banco.banco}`, columnasPdf, filasDatos, {
        subtitulo: `Desde ${fechaInicio}`,
        fileName: `EdoCtaBanco-${banco.banco}`,
        orientation: 'portrait',
        footRows: [
          ['', '', '', '', 'Saldo Anterior', '', money(saldoAnterior)],
          ['', '', '', '', 'Cargos del Periodo', '', money(totales.cargo + totales.comision)],
          ['', '', '', '', 'Abonos del Periodo', '', money(totales.abono)],
          ['', '', '', '', 'Saldo Actual', '', money(saldoActual)],
        ],
      });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'No se pudo exportar', text: e.message });
    } finally {
      setExportando(null);
    }
  }

  function exportarExcel() {
    setExportando('excel');
    try {
      exportTableExcel(`Estado de Cuenta - ${banco.banco}`, columnasExcel, filasDatos, { fileName: `EdoCtaCliente-${banco.banco}`, sheetName: 'EstadoCuenta' });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'No se pudo exportar', text: e.message });
    } finally {
      setExportando(null);
    }
  }

  return (
    <Modal
      open onClose={onClose} title={`Estado Cuenta de ${banco.banco}`} width="max-w-5xl"
      footer={<button type="button" onClick={onClose} className="btn-primary">Cerrar</button>}
    >
      <div className="flex flex-wrap items-end gap-3 mb-3">
        <button type="button" onClick={exportarPDF} disabled={exportando === 'pdf' || filasDatos.length === 0} className="btn-outline text-xs px-3 py-1.5">
          {exportando === 'pdf' ? 'Generando...' : 'PDF'}
        </button>
        <button type="button" onClick={exportarExcel} disabled={exportando === 'excel' || filasDatos.length === 0} className="btn-outline text-xs px-3 py-1.5">
          {exportando === 'excel' ? 'Generando...' : 'Excel'}
        </button>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Fecha</label>
          <input
            type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)}
            className="input-base"
          />
        </div>
        <h4 className="ml-auto text-base font-semibold text-slate-900">
          Saldo <span className="ml-2 rounded-full bg-danger-500 px-3 py-1 text-white">{money(saldoBanco)}</span>
        </h4>
        {error && <span className="text-sm text-danger-600">{error}</span>}
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-3 py-2 text-center">Fecha</th>
              <th className="px-3 py-2 text-center">Movimiento</th>
              <th className="px-3 py-2 text-center">Observaciones</th>
              <th className="px-3 py-2 text-center">Numero</th>
              <th className="px-3 py-2 text-center">Cargos</th>
              <th className="px-3 py-2 text-center">Comision</th>
              <th className="px-3 py-2 text-center">Abonos</th>
              <th className="px-3 py-2 text-center">Saldo</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {cargando && <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-400">Cargando...</td></tr>}
            {!cargando && filas.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-400">Sin movimientos</td></tr>}
            {!cargando && filas.map((f, i) => (
              <tr key={i}>
                <td className="px-3 py-2 text-center whitespace-nowrap">{f.esSaldoAnterior ? 'NA' : fmtFecha(f.fecha)}</td>
                <td className="px-3 py-2 text-center whitespace-nowrap">{f.movimiento}</td>
                <td className={`px-3 py-2 ${f.esSaldoAnterior ? 'text-center' : ''}`}>{f.observaciones}</td>
                <td className="px-3 py-2 text-center whitespace-nowrap">{f.numero}</td>
                <td className="px-3 py-2 text-center whitespace-nowrap">{moneyNA(f.cargo)}</td>
                <td className="px-3 py-2 text-center whitespace-nowrap">{moneyNA(f.comision)}</td>
                <td className="px-3 py-2 text-center whitespace-nowrap">{moneyNA(f.abono)}</td>
                <td className="px-3 py-2 text-center whitespace-nowrap">{money(f.saldoCorrido)}</td>
              </tr>
            ))}
            {!cargando && [
              ['Saldo Anterior', saldoAnterior],
              ['Cargos del Periodo', totales.cargo + totales.comision],
              ['Abonos del Periodo', totales.abono],
              ['Actual Saldo', saldoActual],
            ].map(([lbl, val]) => (
              <tr key={lbl}>
                <td colSpan={6} />
                <td className="px-3 py-2 font-bold">{lbl}</td>
                <td className="px-3 py-2 text-center font-bold">{money(val)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}
